import Button from '@material-ui/core/Button';
import clsx from 'clsx';
import React from 'react';
import { CheckBoxGroup } from '../../../components/checkbox';
import MyRadio from '../../../components/radio';
import Search from '../../../components/search';
import MySwitch from '../../../components/switch';
import _fetchApi from '../../../fetch';
import GlobalContext from '../../../global';
import { getTaskIcon } from '../Tasks';

const DIFFICULTIES = [
  { label: "Простая", icon: 0, },
  { label: "Средняя", icon: 30, },
  { label: "Сложная", icon: 50, },
  { label: "Очень сложная", icon: 70, },
  { label: "Невыполнимая", icon: 90, },
];

function Separator(props) {
  return <svg height="2" viewBox="0 0 298 1" fill="none" xmlns="http://www.w3.org/2000/svg" className="tasklist-separator" {...props}>
    <line y1="0.5" x2="298" y2="0.5" stroke="var(--alert-separator)" />
  </svg>;
}

// "1,3,4" (1-based indexes from the router state) -> [true, false, true, true, ...]; empty -> everything selected
function parseSelection(value, length) {
  if (!value) {
    return Array(length).fill(true);
  }
  const selected = value.split(",").map(Number);
  return Array.from({ length, }, (_, index) => selected.includes(index + 1));
}

// [true, false, true, true, ...] -> "1,3,4" (the inverse of parseSelection)
function stringifySelection(selected) {
  return selected.map((is_selected, index) => is_selected ? index + 1 : "").filter(Boolean).join(",");
}

/*
  Search + filters of the task archive. Rendered exactly once: it triggers the first load of the list.
  Desktop: the left column. Below 1100px: a sticky toolbar (search + "Фильтры" button) and the filters
  become a sheet (CSS only, the markup stays mounted).
*/
export class ListFilter extends React.Component {
  static contextType = GlobalContext;

  constructor(props) {
    super(props);

    const state = props.location.state;
    // the public list hands the text search back as "query", the private one as "search"
    this.initial_search = state?.search || state?.query || new URLSearchParams(window.location.search).get("query") || "";

    this.state = {
      hide_solved: !state?.hidesolved ? true : state.hidesolved === "1",
      sort_by: state?.order || "id",
      difficulty_selected: parseSelection(state?.difficulties, DIFFICULTIES.length),
      categories_selected: undefined,
      categories_verbose: window.sessionStorage.getItem("task_categories") ? Object.keys(JSON.parse(window.sessionStorage.getItem("task_categories"))) : undefined,
      has_search_text: Boolean(this.initial_search),
      reversed: !state?.reversed ? false : state.reversed === "1",
      sheet_open: false,
    };

    this.search_timeout = null;
    this.panel_ref = React.createRef();
    this.toggle_ref = React.createRef();

    this.openSheet = () => this.setState({ sheet_open: true, });
    this.closeSheet = () => this.setState({ sheet_open: false, });
    this.handleKeyDown = event => {
      if (event.key === "Escape") {
        this.closeSheet();
      }
    };
    // the sheet only exists below 1100px: close it when the window grows past that (e.g. a rotated tablet)
    this.handleDesktopQuery = event => {
      if (event.matches) {
        this.closeSheet();
      }
    };
  }

  componentDidMount() {
    const init = categories_verbose => this.setState({
      categories_verbose: categories_verbose,
      categories_selected: parseSelection(this.props.location.state?.categories, categories_verbose.length),
    }, () => this.makeSearch(this.initial_search, true));

    if (this.state.categories_verbose) {
      init(this.state.categories_verbose);
    } else {
      _fetchApi(`/getAvaliableCategories`, this.context.token ? {
        method: "GET",
        headers: { "Authorization": "Bearer " + this.context.token, },
      } : { method: "GET", }).then(response => response.json()).then(result => {
        const sorted = Object.fromEntries(Object.entries(result).sort(([, a], [, b]) => a - b));
        window.sessionStorage.setItem("task_categories", JSON.stringify(sorted));
        init(Object.keys(sorted));
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.category !== this.props.category) {
      this.search_timeout = setTimeout(() => this.makeSearch(document.getElementById("task-list-search").value, true));
    }
    if (prevState.sheet_open !== this.state.sheet_open) {
      this.lockPage(this.state.sheet_open);
      if (this.state.sheet_open) {
        this.panel_ref.current.focus({ preventScroll: true, });
      } else {
        this.toggle_ref.current?.focus({ preventScroll: true, });
      }
    }
  }

  componentWillUnmount() {
    clearTimeout(this.search_timeout);
    if (this.state.sheet_open) {
      this.lockPage(false);
    }
  }

  lockPage(locked) {
    document.documentElement.classList.toggle("scroll-locked", locked);
    this.desktop_query = this.desktop_query || window.matchMedia("(min-width: 1100px)");
    if (locked) {
      document.addEventListener("keydown", this.handleKeyDown);
      this.desktop_query.addListener(this.handleDesktopQuery);
    } else {
      document.removeEventListener("keydown", this.handleKeyDown);
      this.desktop_query.removeListener(this.handleDesktopQuery);
    }
  }

  // keep the filter in the /tasks history entry (in the format the task page hands back to "← К списку задач"),
  // so browser / swipe Back from a task restores it too
  saveFilter(value) {
    this.props.history.replace({
      ...this.props.location,
      state: {
        category: this.props.category,
        search: value || "",
        order: this.state.sort_by,
        reversed: this.state.reversed ? "1" : "0",
        hidesolved: this.state.hide_solved ? "1" : "0",
        difficulties: stringifySelection(this.state.difficulty_selected),
        categories: this.state.categories_selected && stringifySelection(this.state.categories_selected),
      },
    });
  }

  makeSearch(value, first_load = false) {
    clearTimeout(this.search_timeout);
    this.saveFilter(value);

    if (this.props.category === "private") {
      this.props.updateTasks({
        search: value,
      });
    } else {
      if (value) {
        this.props.updateTasks({
          search: value,
        });
      } else {
        const update = () => this.props.updateTasks({
          hide_solved: this.state.hide_solved,
          sort_by: this.state.sort_by,
          difficulty_selected: this.state.difficulty_selected,
          categories_selected: this.state.categories_selected,
          reversed: this.state.reversed,
        });
        if (first_load) {
          update();
        } else {
          this.search_timeout = setTimeout(update, 500);
        }
      }
    }
  }

  // how many filter groups differ from the defaults (badge on the "Фильтры" button)
  changedFilters() {
    return [
      this.state.sort_by !== "id",
      this.state.reversed,
      !this.state.difficulty_selected.every(Boolean),
      this.state.categories_selected && !this.state.categories_selected.every(Boolean),
    ].filter(Boolean).length;
  }

  render() {
    const changed = this.changedFilters();

    return <div className="task-list-filter">
      <div className="task-filter-toolbar">
        <Search
          id="task-list-search"
          value={this.initial_search}
          placeholder="Поиск по названию"
          onTypingEnd={value => this.makeSearch(value)}
          onValueChange={value => {
            if (Boolean(value) !== this.state.has_search_text) {
              this.setState({ has_search_text: Boolean(value), });
            }
          }} />
        {this.props.category !== "private" && <button
          type="button"
          className="task-filter-toggle"
          ref={this.toggle_ref}
          aria-expanded={this.state.sheet_open}
          aria-controls="task-filter-panel"
          disabled={this.state.has_search_text}
          title={this.state.has_search_text ? "Фильтры не действуют при поиске по названию" : undefined}
          onClick={this.openSheet}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M3 5.5H11M15 5.5H17M3 14.5H5M9 14.5H17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <circle cx="13" cy="5.5" r="2" stroke="currentColor" strokeWidth="1.6" />
            <circle cx="7" cy="14.5" r="2" stroke="currentColor" strokeWidth="1.6" />
          </svg>
          <span className="task-filter-toggle__label">Фильтры</span>
          {changed > 0 && !this.state.has_search_text && <span className="task-filter-count" aria-label={"изменено: " + changed}>{changed}</span>}
        </button>}
      </div>
      <div className="task-filter-or" aria-hidden="true">
        <span>или</span>
      </div>
      <div className={clsx("task-filter-sheet", this.state.sheet_open && "open")}>
        <div className="task-filter-backdrop" onClick={this.closeSheet} />
        <div
          className="task-filter-panel"
          id="task-filter-panel"
          ref={this.panel_ref}
          tabIndex={-1}
          role={this.state.sheet_open ? "dialog" : undefined}
          aria-modal={this.state.sheet_open || undefined}
          aria-label="Фильтры"
        >
          <p className="task-filter-panel__title">Фильтры</p>
          <div className="task-filter-body">
            {/* TODO: undo <MyCheckBox
                  label="Скрыть решённые"
                  checked={this.state.hide_solved}
                  className="hide-solved"
                  onChange={ () => this.setState({hide_solved: !this.state.hide_solved,}, () => {
                    this.makeSearch();
                  }) }
                />
                <Separator style={ {paddingTop: "28px",} } /> */}
            <MyRadio
              containerClassName="sort-form"
              formLabel="Сортировка"
              onChange={event => this.setState({ sort_by: event.target.value, }, () => this.makeSearch())}
              value={this.state.sort_by}
              radios={[
                {
                  label: "По новизне",
                  value: "id",
                },
                {
                  label: "По сложности",
                  value: "difficulty",
                },
                {
                  label: "По количеству решивших",
                  value: "solvedby",
                },
              ]} />
            <MySwitch
              checked={this.state.reversed}
              label="Обратный порядок"
              style={{ paddingTop: "24px", }}
              onChange={() => this.setState({ reversed: !this.state.reversed, }, () => this.makeSearch())} />
            <Separator />
            <CheckBoxGroup
              containerClassName="sort-form"
              formLabel="Сложность"
              checkboxes={DIFFICULTIES.map((difficulty, index) => ({
                label: <span className="checkbox-difficulty-label">
                  {getTaskIcon(difficulty.icon)}
                  <span className="MuiTypography-root MuiFormControlLabel-label MuiTypography-body1 checkbox-difficulty">{difficulty.label}</span>
                </span>,
                checked: this.state.difficulty_selected[index],
                onChange: () => this.setState({
                  difficulty_selected: this.state.difficulty_selected.map((selected, i) => i === index ? !selected : selected),
                }, () => this.makeSearch()),
              }))} />
            <Separator style={{ paddingTop: "28px", }} />
            {this.state.categories_selected && <div className="task-filter-categories">
              <CheckBoxGroup
                containerClassName="sort-form"
                formLabel="Темы"
                checkboxes={this.state.categories_verbose.map((element, index) => ({
                  label: element,
                  checked: this.state.categories_selected[index],
                  onChange: () => this.setState({
                    categories_selected: this.state.categories_selected.map((selected, i) => i === index ? !selected : selected),
                  }, () => this.makeSearch()),
                }))} />
              <Button
                disableElevation
                disableRipple
                className="categories-select-all"
                onClick={() => {
                  const select = !this.state.categories_selected.every(Boolean);
                  this.setState({
                    categories_selected: this.state.categories_selected.map(() => select),
                  }, () => this.makeSearch());
                }}>Выбрать всё</Button>
            </div>}
            {(this.state.has_search_text || this.props.category === "private") && <div className="task-filter-dim" />}
          </div>
          <div className="task-filter-panel__footer">
            <button type="button" className="task-filter-apply" onClick={this.closeSheet}>Показать задачи</button>
          </div>
        </div>
      </div>
    </div>;
  }
}
